// eslint-disable-next-line no-param-reassign
import { Swiper, FreeMode, Mousewheel } from 'swiper';

const TABLIST_SELECTOR = '[role="tablist"]';

function updateContainerAccessibility(swiper, swiperContainer) {
  const { isLocked } = swiper;

  if (isLocked) {
    swiperContainer.removeAttribute('tabindex');
    swiperContainer.removeAttribute('role');
  } else {
    swiperContainer.setAttribute('tabindex', '0');
    swiperContainer.setAttribute('role', 'region');
  }
}

function bindSwiperKeyboard(swiperContainer, swiperInstance) {
  const handler = (event) => {
    if (swiperInstance.isLocked) {
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      swiperInstance.slideNext();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      swiperInstance.slidePrev();
    }
  };

  // eslint-disable-next-line no-param-reassign
  swiperContainer.cardsStandardKeydownHandler = handler;
  swiperContainer.addEventListener('keydown', handler);
}

function createCardsSwiper(swiperContainer, block) {
  if (swiperContainer.swiper) {
    return swiperContainer.swiper;
  }

  const swiperInstance = new Swiper(swiperContainer, {
    direction: 'horizontal',
    slidesPerView: 'auto',
    loop: false,
    modules: [FreeMode, Mousewheel],
    grabCursor: true,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumBounce: false,
      momentumRatio: 1,
      momentumVelocityRatio: 1,
      minimumVelocity: 0.02,
      sticky: false,
    },
    mousewheel: {
      enabled: true,
      forceToAxis: true,
      releaseOnEdges: true,
    },
    breakpoints: {
      1440: { slidesPerView: 4, spaceBetween: 32 },
    },
    on: {
      init(swiper) {
        // eslint-disable-next-line no-param-reassign
        swiper.allowTouchMove = !swiper.isLocked;
        swiperContainer.classList.toggle('swiper-grab-cursor', !swiper.isLocked);
        block.classList.toggle('cards-standard--locked', swiper.isLocked);
        updateContainerAccessibility(swiper, swiperContainer);
      },
      resize(swiper) {
        // eslint-disable-next-line no-param-reassign
        swiper.allowTouchMove = !swiper.isLocked;
        swiperContainer.classList.toggle('swiper-grab-cursor', !swiper.isLocked);
        block.classList.toggle('cards-standard--locked', swiper.isLocked);
        updateContainerAccessibility(swiper, swiperContainer);
      },
    },
  });

  bindSwiperKeyboard(swiperContainer, swiperInstance);
  // eslint-disable-next-line no-param-reassign
  swiperContainer.cardsStandardSwiper = swiperInstance;
  return swiperInstance;
}

function initCardsTabs(block) {
  if (block.getAttribute('data-cards-tabs-init') === '1') {
    return;
  }

  const tablist = block.querySelector(TABLIST_SELECTOR);

  if (!tablist) {
    return;
  }

  const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
  const panels = Array.from(block.querySelectorAll('[role="tabpanel"]'));

  if (tabs.length < 2 || panels.length < 2 || tabs.length !== panels.length) {
    return;
  }

  block.setAttribute('data-cards-tabs-init', '1');

  const activate = (index) => {
    tabs.forEach((tabElement, i) => {
      const selected = i === index;
      tabElement.setAttribute('aria-selected', selected ? 'true' : 'false');
      tabElement.setAttribute('tabindex', selected ? '0' : '-1');
      tabElement.classList.toggle('cards-standard__tabs-item--active', selected);
    });

    panels.forEach((panelElement, i) => {
      if (i === index) {
        panelElement.removeAttribute('hidden');
        requestAnimationFrame(() => {
          const swiperEl = panelElement.querySelector('.cards-standard__swiper.swiper');

          if (!swiperEl) {
            return;
          }

          if (swiperEl.swiper) {
            const { swiper } = swiperEl;

            swiper.slideTo(0, 0, false);
            swiper.setTranslate(0);

            setTimeout(() => {
              swiper.updateSize();
              swiper.update();
              swiper.setTranslate(0);

              block.classList.toggle('cards-standard--locked', swiper.isLocked);
              updateContainerAccessibility(swiper, swiperEl);
            }, 50);
          } else {
            createCardsSwiper(swiperEl, block);
          }
        });
      } else {
        panelElement.setAttribute('hidden', '');
      }
    });
  };

  tabs.forEach((tabElement, index) => {
    tabElement.addEventListener('click', () => {
      activate(index);
    });

    tabElement.addEventListener('keydown', (event) => {
      let nextIndex = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (index + 1) % tabs.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = tabs.length - 1;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        activate(nextIndex);
        tabs[nextIndex].focus();
      }
    });
  });

  activate(0);
}

function destroyCardsStandardSwipersInBlock(block) {
  block.querySelectorAll('.cards-standard__swiper.swiper').forEach((swiperContainer) => {
    if (swiperContainer.cardsStandardKeydownHandler) {
      swiperContainer.removeEventListener('keydown', swiperContainer.cardsStandardKeydownHandler);
      // eslint-disable-next-line no-param-reassign
      delete swiperContainer.cardsStandardKeydownHandler;
    }

    if (swiperContainer.swiper) {
      swiperContainer.swiper.destroy(true, true);
    }

    // eslint-disable-next-line no-param-reassign
    delete swiperContainer.cardsStandardSwiper;
  });
}

function initCardsStandardBlock(block) {
  if (block.hasAttribute('data-cards-tabs')) {
    initCardsTabs(block);
    return;
  }

  const swiperContainer = block.querySelector('.cards-standard__swiper.swiper');

  if (!swiperContainer) {
    return;
  }

  createCardsSwiper(swiperContainer, block);
}

export default function initCardsStandardSlider() {
  document.querySelectorAll('.cards-standard').forEach((block) => {
    initCardsStandardBlock(block);
  });
}

function resetAndInitAll() {
  document.querySelectorAll('.cards-standard').forEach((block) => {
    block.removeAttribute('data-cards-tabs-init');
    destroyCardsStandardSwipersInBlock(block);
  });
  initCardsStandardSlider();
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.acf) {
    window.acf.addAction('render_block_preview/type=azds/cards-standard-updated', () => {
      resetAndInitAll();
    });
  }

  initCardsStandardSlider();
});
